// src/pages/StudentCohorts.tsx
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import { ref, onValue, get, child } from "firebase/database";

type Group = {
  id: string;
  name: string;
  createdAt?: string;
  assignedProcedures?: Record<string, true>;
  procedures?: Record<string, true>; // legacy free-typed names
  members?: Record<string, true>;
};

type Attempt = {
  ts?: number | string;
  score?: number;
  procId?: string;
  name?: string;
};

function toList<T>(node: any): T[] {
  if (!node) return [];
  return Array.isArray(node) ? node : Object.values(node);
}

function tsMax(list: Attempt[]): number | undefined {
  const v = list
    .map((a) =>
      typeof a.ts === "number"
        ? a.ts
        : typeof a.ts === "string"
        ? Date.parse(a.ts)
        : 0
    )
    .reduce((m, x) => (x > m ? x : m), 0);
  return v || undefined;
}

export default function StudentCohorts() {
  const loc = useLocation() as any;
  const college: string =
    loc?.state?.college ||
    (sessionStorage.getItem("college") ?? "UNKNOWN_COLLEGE");
  const uid = auth.currentUser?.uid || "";

  const [loading, setLoading] = React.useState(true);
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [rows, setRows] = React.useState<
    {
      groupId: string;
      groupName: string;
      assignedCount: number;
      attemptedCount: number;
      coverage: number; // 0..100
      attemptsTotal: number;
      last?: number;
      perProcedure: { idOrName: string; done: boolean; attempts: number }[];
    }[]
  >([]);

  // live groups → filter to "where members[uid] is true"
  React.useEffect(() => {
    const gref = ref(db, `Colleges/${college}/Groups`);
    const unsub = onValue(gref, (snap) => {
      const v = (snap.val() || {}) as Record<string, any>;
      const all: Group[] = Object.entries(v).map(([id, g]) => ({
        id,
        name: g?.name || "(untitled)",
        createdAt: g?.createdAt,
        assignedProcedures: g?.assignedProcedures || null,
        procedures: g?.procedures || null,
        members: g?.members || {},
      }));
      const mine = all.filter((g) => !!g.members?.[uid]);
      setGroups(mine);
    });
    return () => unsub();
  }, [college, uid]);

  // compute analytics for *this* student per cohort
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const root = ref(db);

      // pull student's Game Modes once
      const gmSnap = await get(
        child(root, `Colleges/${college}/Users/${uid}/Game Modes`)
      );
      const gm = gmSnap.val() || {};

      // normalize attempts across shapes
      const attempts: Attempt[] = [];
      const cpNode =
        gm["Clinical Procedures"] || gm["ClinicalProcedures"] || null;
      if (cpNode && typeof cpNode === "object") {
        Object.keys(cpNode).forEach((key) => {
          const arr = toList<Attempt>(cpNode[key]);
          arr.forEach((a) => attempts.push({ ...a, procId: key }));
        });
      }
      Object.keys(gm || {}).forEach((k) => {
        const arr = toList<Attempt>(gm[k]);
        arr.forEach((a) => {
          if (a && (a.procId || a.name)) attempts.push(a as Attempt);
        });
      });

      const rowsComputed: typeof rows = [];

      for (const g of groups) {
        const assignedIds = Object.keys(g.assignedProcedures || {});
        const legacyNames = Object.keys(g.procedures || {});
        const denom = assignedIds.length
          ? assignedIds.length
          : legacyNames.length;

        // build per-procedure status for UI
        const perProcedure: {
          idOrName: string;
          done: boolean;
          attempts: number;
        }[] = [];

        if (assignedIds.length) {
          assignedIds.forEach((pid) => {
            const mine = attempts.filter((a) => a.procId === pid);
            perProcedure.push({
              idOrName: pid,
              done: mine.length > 0,
              attempts: mine.length,
            });
          });
        } else if (legacyNames.length) {
          legacyNames.forEach((nm) => {
            const key = nm.trim().toLowerCase();
            const mine = attempts.filter(
              (a) => (a.name || "").trim().toLowerCase() === key
            );
            perProcedure.push({
              idOrName: nm,
              done: mine.length > 0,
              attempts: mine.length,
            });
          });
        }

        const attemptedCount = perProcedure.filter((p) => p.done).length;
        const coverage = denom ? Math.round((attemptedCount / denom) * 100) : 0;

        rowsComputed.push({
          groupId: g.id,
          groupName: g.name,
          assignedCount: denom,
          attemptedCount,
          coverage,
          attemptsTotal: attempts.length,
          last: tsMax(attempts),
          perProcedure,
        });
      }

      // sort: coverage desc, then last desc
      rowsComputed.sort((a, b) => {
        if (b.coverage !== a.coverage) return b.coverage - a.coverage;
        return (b.last || 0) - (a.last || 0);
      });

      setRows(rowsComputed);
      setLoading(false);
    })();
  }, [college, uid, groups]);

  return (
    <div className="container">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>My Cohorts</h2>
        <Link className="btn" to="/s" state={{ college }}>
          Back to Dashboard
        </Link>
      </div>

      {loading ? (
        <div className="card">
          <p>Loading…</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="card">
          <p className="small">You are not part of any cohort yet.</p>
        </div>
      ) : (
        rows.map((r) => (
          <div key={r.groupId} className="card" style={{ marginBottom: 12 }}>
            <div className="row">
              <h3 style={{ margin: 0 }}>{r.groupName}</h3>
              <span className="small" style={{ opacity: 0.8 }}>
                Assigned: {r.assignedCount} • Covered: {r.attemptedCount}/
                {r.assignedCount} ({r.coverage}%)
                {r.last
                  ? ` • Last activity: ${new Date(r.last).toLocaleString()}`
                  : ""}
              </span>
            </div>

            {/* Per-procedure status */}
            {r.perProcedure.length === 0 ? (
              <p className="small" style={{ marginTop: 8 }}>
                No procedures assigned yet.
              </p>
            ) : (
              <table className="table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Procedure</th>
                    <th>Status</th>
                    <th>Attempts</th>
                  </tr>
                </thead>
                <tbody>
                  {r.perProcedure.map((p, idx) => (
                    <tr key={idx}>
                      <td>{p.idOrName}</td>
                      <td>{p.done ? "Started" : "Not started"}</td>
                      <td>{p.attempts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))
      )}
    </div>
  );
}
